import React, { useEffect, useState } from "react";
import { Button, Col, FormGroup, Modal } from "react-bootstrap";
import InputField from "../../../InputField";
import { clientEventRegistrationCreate, clientEventRegistrationUpdate } from "../../../../../api/cp";
import { eventRegistrationRoleOptions, eventRegistrationStatusOptions } from "../models";
import { useEventContext } from "../../../../context";
import { mapValueToSelectOption } from "../tools";
import { ROLE_VIEWER, STATUS_APPROVED } from "../constants";
import { useLocaleContext } from "../../../../context/LocaleContext";
import UploadPhotoModal from "./UploadPhotoModal";
import InputOrganization from "../../../InputOrganization";

const HYBRID = 'hybrid';
const TYPE_PHOTO = 'photo';
const AVAILABLE_TYPES = ['online', 'offline'];

export const ClientEditForm = ({ email, role, status, getDataType, participationType, info, fields, entries, disabled, sendMail, onChange, onUpdate }) => {
    const { event } = useEventContext();
    const $locale = useLocaleContext();
    const isFillComplete = email && fields.filter(({ required }) => required).map(({ name }) => name).every((field) => !!info[field]);
    const [isEmailRegistered, setEmailRegistered] = useState(false);
    const [isUploadEditorVisible, setUploadEditorVisible] = useState(false);
    const [photoSrc, setPhotoSrc] = useState();
    const participationTypesOptions = AVAILABLE_TYPES.map((field) => mapValueToSelectOption(field, 'participationType'));

    useEffect(() => {
        if (info.photo && typeof info.photo === 'object') {
            updatePhotoSrc(info.photo);

            return;
        }

        setPhotoSrc(info.photo);
    }, [info.photo]);

    useEffect(() => {
        if (!isFillComplete) {
            onChange('sendMail', false);
        }
    }, [isFillComplete]);

    const updatePhotoSrc = () => {
        const url = URL.createObjectURL(info.photo);
        setPhotoSrc(url);
    };

    const handleCheckEmail = (email) => {
        entries.forEach((entry) => {
            if (entry.client.email === email) {
                setEmailRegistered(true);
                return;
            }
        })
    };

    return (
        <>
            <form style={{ display: 'flex', columnGap: '20px' }}>
                {!!photoSrc && (
                    <div style={{ maxWidth: '300px' }}>
                        <img
                            src={photoSrc}
                            alt="Фотография"
                            style={{ width: '100%' }}
                        />
                    </div>
                )}
                <div style={{ width: '400px' }}>
                    <FormGroup>
                        <InputField
                            type="email"
                            name="email"
                            required={true}
                            label="Email"
                            placeholder={"user@domain.com"}
                            value={email}
                            onChange={(value) => {
                                onChange('email', value);
                                setEmailRegistered(false);
                            }}
                            onBlur={() => handleCheckEmail(email)}
                            disabled={disabled}
                        />
                        {isEmailRegistered && (
                            <p style={{ marginTop: '4px', color: '#FF121D' }}>
                                Участник с таким e-mail уже зарегистрирован
                            </p>
                        )}
                    </FormGroup>
                    {fields.map((field) => (
                        <FormGroup key={field.name}>
                            {field.type === TYPE_PHOTO
                                ? <Button
                                    onClick={() => setUploadEditorVisible(true)}
                                >
                                    Добавить фото
                                </Button>
                                : field.type === 'organization' ? <InputOrganization
                                    eventKey={event.eventKey}
                                    name={field.name}
                                    value={info.organizationName}
                                    label={field.label}
                                    onChange={(value) => onUpdate(value)}
                                />
                                    : <InputField
                                        type={getDataType(field.type)}
                                        name={field.name}
                                        // required={field.required}
                                        label={field.label}
                                        placeholder={field.placeholder}
                                        options={field.options?.map(({ value, label_ru: label }) => ({ value, label }))}
                                        value={info[field.name]}
                                        onChange={(value) => onChange(field.name, value)}
                                        disabled={disabled}
                                    />
                            }
                        </FormGroup>
                    ))}
                    <FormGroup>
                        <InputField
                            type="select"
                            name="participationType"
                            required={true}
                            label={"Формат участия"}
                            value={participationType}
                            onChange={(value) => onChange('participationType', value)}
                            options={participationTypesOptions}
                            disabled={disabled || event.participationType !== HYBRID}
                        />
                    </FormGroup>
                    <FormGroup>
                        <InputField
                            type="select"
                            name="role"
                            required={true}
                            label={"Роль"}
                            value={(role)}
                            onChange={(value) => onChange('role', value)}
                            options={eventRegistrationRoleOptions}
                            disabled={disabled}
                        />
                    </FormGroup>
                    <FormGroup>
                        <InputField
                            type="select"
                            name="status"
                            required={true}
                            label={"Статус"}
                            value={(status)}
                            onChange={(value) => onChange('status', value)}
                            options={eventRegistrationStatusOptions}
                            disabled={disabled}
                        />
                    </FormGroup>
                    <hr />
                    <FormGroup>
                        <InputField
                            type="checkbox"
                            name="sendMail"
                            label="Отправить письмо о регистрации"
                            value={sendMail}
                            onChange={(value) => onChange('sendMail', value)}
                            disabled={!isFillComplete}
                        />
                    </FormGroup>
                </div>
            </form>
            <UploadPhotoModal
                $locale={$locale}
                isModalOpen={isUploadEditorVisible}
                onChange={(value) => onChange(TYPE_PHOTO, value)}
                onClose={() => setUploadEditorVisible(false)}
            />
        </>
    );
}

export const ClientEditModal = ({ eventRegistration, getDataType, fields, entries, event, onClose, onChange }) => {
    const [email, setEmail] = useState(eventRegistration?.client?.email || '');
    const [role, setRole] = useState(eventRegistration?.role || '');
    const [status, setStatus] = useState(eventRegistration?.status || '');
    const [participationType, setParticipationType] = useState(eventRegistration?.participationType || '')
    const [info, setInfo] = useState(eventRegistration?.info || {});
    const [sendMail, setSendMail] = useState(false);
    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
        setEmail(eventRegistration?.client?.email || '');
        setRole(eventRegistration?.role || ROLE_VIEWER);
        setStatus(eventRegistration?.status || STATUS_APPROVED);
        setParticipationType(eventRegistration?.participationType || event.participationType);
        setInfo(eventRegistration?.info || {});
        setDisabled(false);
    }, [eventRegistration]);

    const handleUpdateInfo = (data) => {
        setInfo(info => ({ ...info, ...data }));
    }

    const handleChange = (name, value) => {
        switch (name) {
            case 'email': return setEmail(value);
            case 'role': return setRole(value);
            case 'status': return setStatus(value);
            case 'participationType': return setParticipationType(value);
            case 'sendMail': return setSendMail(value);
            default: setInfo({ ...info, [name]: value });
        }
    }

    const handleSave = async () => {

        setDisabled(true);
        try {
            const updatedInfo = {
                ...info,
                email,
                participationType,
                role,
                status,
                sendMail,
            };

            const formData = new FormData();
            Object.keys(updatedInfo).forEach(key => formData.append(key, updatedInfo[key]));

            if (updatedInfo.organizationInn || updatedInfo.organizationKpp || updatedInfo.organizationName) {
                ['organizationInn', 'organizationKpp', 'organizationName']
                    .forEach(key => formData.append(`konturFocusOrg[${key}]`, info[key] || ''));
            }

            if (eventRegistration?.id) {
                const result = await clientEventRegistrationUpdate(event.eventKey, eventRegistration.id, formData);
                await onChange(result);
            } else {
                const result = await clientEventRegistrationCreate(event.eventKey, formData);
                await onChange(result);
            }
            onClose();
        } catch (e) {
            console.error(e);
            setDisabled(false);
        }
    }

    return (
        <Modal
            className="client-modal"
            show={!!eventRegistration}
            onHide={onClose}
        >
            <Modal.Header closeButton>
                <Modal.Title>Данные участника мероприятия</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {eventRegistration && <ClientEditForm
                    email={email}
                    role={role}
                    status={status}
                    getDataType={getDataType}
                    info={info}
                    participationType={participationType}
                    sendMail={sendMail}
                    fields={fields}
                    entries={entries}
                    disabled={disabled}
                    onChange={handleChange}
                    onUpdate={handleUpdateInfo}
                />}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={handleSave} bsStyle="primary" disabled={disabled}>Сохранить</Button>
                <Button onClick={onClose}>Закрыть</Button>
            </Modal.Footer>
        </Modal>
    );
}
